import {Button, Card, DataStatus} from 'platform/components';
import {Box, HStack, Text} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';

import {isNil, mergeAll} from 'ramda';

import {
  useGetDatagridSynchronizationServiceItemsExternalHistoryQuery,
  usePostDatagridSynchronizeServiceItemsExternalHistoryMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, Nullish, RequiredTestIdProps, parseDate, suffixTestId} from 'shared/web';

import type {ActionCallback, QueryFilterObject} from 'features/eag-grid';

import {handleApiError} from '../../utils/handleApiError';
import {refreshDmsDatagrid} from '../../utils/refreshDmsDatagrid';
import {DmsDataGrid} from '../DmsDataGrid/DmsDataGrid';

interface WorkItemsProps extends RequiredTestIdProps {
  vehicleId: string | Nullish;
  datagridCode: string;
  actionCallback?: ActionCallback;
}

export function WorkItems(props: WorkItemsProps) {
  const formatDateTime = useDateTimeFormatter();

  const [postDatagridSynchronizeServiceItemsExternalHistory, {isLoading: isSyncRunning}] =
    usePostDatagridSynchronizeServiceItemsExternalHistoryMutation();

  const {data, isLoading, isError} = useGetDatagridSynchronizationServiceItemsExternalHistoryQuery(
    {vehicleId: props.vehicleId ?? ''},
    {skip: isNil(props.vehicleId)}
  );

  const handleSync = () =>
    postDatagridSynchronizeServiceItemsExternalHistory({
      body: {vehicleId: props.vehicleId ?? ''},
    })
      .unwrap()
      .then(() => refreshDmsDatagrid(props.datagridCode))
      .catch(handleApiError);

  const queryModifier = (filter: QueryFilterObject) =>
    mergeAll([filter, {vehicleId: props.vehicleId ?? null}]);

  return (
    <DataStatus
      isLoading={isNil(props.vehicleId) || isLoading}
      isError={isError}
      key="historyOfRepairs-work-items"
      minHeight={175}
    >
      <Card>
        <HStack align="center" justify="flex-end">
          <HStack align="center" spacing={2}>
            <Text size="small" color="secondary">
              {`${i18n.t('entity.workItems.labels.lastSync')}: ${data ? formatDateTime('dateTimeLong', parseDate(data?.synchronizedAt)) : EMPTY_PLACEHOLDER}`}
            </Text>

            <Button
              onClick={handleSync}
              isLoading={isSyncRunning}
              variant="ghostLink"
              size="small"
              title={i18n.t('entity.workItems.actions.synchronize')}
              leftIcon="navigation/refresh"
              data-testid={suffixTestId('syncButton', props)}
            />
          </HStack>
        </HStack>
        <Box height={175}>
          <DmsDataGrid
            gridCode={props.datagridCode}
            queryModifier={queryModifier}
            actionCallback={props.actionCallback}
            data-testid={suffixTestId('workItems', props)}
          />
        </Box>
      </Card>
    </DataStatus>
  );
}
